/** ESL Override / Accept events stored on assessment.notes.level_update_log. */

export type LevelUpdateEventType = "override" | "accept" | "cycle_lock";

export type LevelUpdateLogEntry = {
  id?: string;
  at?: string | null;
  type?: LevelUpdateEventType | string | null;
  account_id?: string | null;
  account_name?: string | null;
  actor_id?: string | null;
  actor_name?: string | null;
  level?: string | null;
  rationale?: string | null;
  /** Formal and Team published together; this entry updated both. */
  combined?: boolean;
};

export type AssessmentNotesWithLevelLog = {
  level_update_log?: LevelUpdateLogEntry[] | null;
  [key: string]: unknown;
} | null;

export const getLevelUpdateLog = (
  notes: AssessmentNotesWithLevelLog | undefined
): LevelUpdateLogEntry[] => {
  const raw = notes?.level_update_log;
  if (!Array.isArray(raw)) return [];
  return raw.filter(entry => entry && typeof entry === "object");
};

/** Latest override/accept timestamp, or null when log is empty. */
export const getLatestLevelUpdateAt = (
  notes: AssessmentNotesWithLevelLog | undefined
): string | null => {
  const log = getLevelUpdateLog(notes);
  if (!log.length) return null;
  let latest: string | null = null;
  let latestMs = -Infinity;
  for (const entry of log) {
    const at = entry.at;
    if (!at) continue;
    const ms = new Date(at).getTime();
    if (!Number.isFinite(ms)) continue;
    if (ms >= latestMs) {
      latestMs = ms;
      latest = at;
    }
  }
  return latest;
};

export const sortLevelUpdateLogNewestFirst = (
  entries: LevelUpdateLogEntry[]
): LevelUpdateLogEntry[] =>
  [...entries].sort((a, b) => {
    const aMs = a.at ? new Date(a.at).getTime() : 0;
    const bMs = b.at ? new Date(b.at).getTime() : 0;
    return (Number.isFinite(bMs) ? bMs : 0) - (Number.isFinite(aMs) ? aMs : 0);
  });

export const levelUpdateEventLabel = (
  type: string | null | undefined
): string => {
  const key = (type || "").toLowerCase();
  if (key === "override") return "Overridden";
  if (key === "accept") return "Disconnect accepted";
  if (key === "cycle_lock") return "Cycle locked";
  return type || "Updated";
};
